import React, { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Search } from 'lucide-react';
import ExportButton from '../../components/common/ExportButton';
import FilterSelect from '../../components/common/FilterSelect';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import { logisticsOrdersAPI } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { useStoreContextStore } from '../../store/storeContextStore';
import { downloadExcelWorkbook } from '../../utils/exportExcel';

const today = () => new Date().toISOString().split('T')[0];
const shiftDate = (value, days) => {
  const date = new Date(`${value || today()}T00:00:00`);
  date.setDate(date.getDate() + days);
  return date.toISOString().split('T')[0];
};
const formatDate = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
const receiptBadgeClass = (value) => ({
  pending: 'badge-warning',
  partial: 'badge-info',
  completed: 'badge-success',
  matched: 'badge-success',
  short: 'badge-warning',
  excess: 'badge-danger',
  recorded: 'badge-info',
}[value] || 'badge-info');
const COMPARISON_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'matched', label: 'Matched' },
  { value: 'unmatched', label: 'Unmatched' },
  { value: 'pending', label: 'Pending' },
];
const computeReceiptStatus = (orderQuantity, receivedQuantity) => {
  if (receivedQuantity === '' || receivedQuantity === null || receivedQuantity === undefined) return 'pending';
  const received = Number(receivedQuantity);
  if (!Number.isFinite(received)) return 'pending';
  if (orderQuantity === null || orderQuantity === undefined || orderQuantity === '') return 'recorded';
  const ordered = Number(orderQuantity);
  if (!Number.isFinite(ordered)) return 'recorded';
  if (received === ordered) return 'matched';
  if (received < ordered) return 'short';
  return 'excess';
};
const computeOrderReceiptProgress = (lineItems = []) => {
  if (!lineItems.length) return 'pending';
  const completedCount = lineItems.filter((item) => item.receivedQuantity !== '' && item.receivedQuantity !== null && item.receivedQuantity !== undefined).length;
  if (completedCount === 0) return 'pending';
  if (completedCount < lineItems.length) return 'partial';
  return 'completed';
};
const parseInputQuantity = (value) => {
  if (value === '') return '';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '';
  return String(Math.max(0, Math.round(numeric)));
};
const computeDifference = (left, right) => {
  if (left === '' || left === null || left === undefined) return null;
  if (right === '' || right === null || right === undefined) return null;
  const leftValue = Number(left);
  const rightValue = Number(right);
  if (!Number.isFinite(leftValue) || !Number.isFinite(rightValue)) return null;
  return leftValue - rightValue;
};
const getComparisonStatus = (left, right) => {
  if (left === '' || left === null || left === undefined) return 'pending';
  if (right === '' || right === null || right === undefined) return 'pending';
  const leftValue = Number(left);
  const rightValue = Number(right);
  if (!Number.isFinite(leftValue) || !Number.isFinite(rightValue)) return 'pending';
  return leftValue === rightValue ? 'matched' : 'unmatched';
};
const orderTypeLabel = (value) => (String(value || 'planned').toLowerCase() === 'unplanned' ? 'Unplanned' : 'Planned');
const orderTypeBadgeClass = (value) => (String(value || 'planned').toLowerCase() === 'unplanned' ? 'badge-warning' : 'badge-info');
const orderStatusLabel = (value) => ({
  pending: 'Pending', confirmed: 'Confirmed', in_transit: 'In Transit',
  delayed: 'Delayed', delivered: 'Delivered', cancelled: 'Cancelled',
}[String(value || 'pending').toLowerCase()] || String(value || 'pending'));
const orderStatusBadgeClass = (value) => ({
  pending: 'badge-warning', confirmed: 'badge-info', in_transit: 'badge-info',
  delayed: 'badge-danger', delivered: 'badge-success', cancelled: 'badge-danger',
}[String(value || 'pending').toLowerCase()] || 'badge-info');
const matchesGoodsReceivedSearch = (order = {}, item = {}, rawQuery = '') => {
  const query = normalizeSearchComparable(rawQuery);
  if (!query) return true;
  const orderText = normalizeSearchComparable([
    order.orderNumber,
    order.type,
    order.storeName,
    order.seller,
    order.itemSummary,
    order.productName,
    order.product_name,
    order.product,
    order.name,
  ].join(' '));
  const itemText = normalizeSearchComparable([
    item.sku,
    item.barcode,
    item.product,
    item.productName,
    item.product_name,
    item.name,
  ].join(' '));
  return orderText.includes(query) || itemText.includes(query);
};
const comparisonBadgeClass = (value) => ({
  matched: 'badge-success',
  unmatched: 'badge-danger',
  pending: 'badge-warning',
}[value] || 'badge-info');
const toPayloadLineItems = (lineItems = []) => lineItems.map((item) => ({
  id: item.id,
  sku: item.sku,
  product: item.product,
  orderQuantity: item.orderQuantity,
  deliveredQuantity: item.deliveredQuantity,
  receivedQuantity: item.receivedQuantity === '' ? null : Number(item.receivedQuantity),
}));
const normalizeSearchQuery = (value) => String(value || '').trim();
const normalizeSearchComparable = (value) => normalizeSearchQuery(value).toLowerCase().replace(/\s+/g, '');

const GoodsReceived = () => {
  const { hasFeatureAccess } = useAuthStore();
  const selectedStore = useStoreContextStore((state) => state.selectedStore);
  const selectedStoreContext = useMemo(() => ({
    storeInfoId: selectedStore?.storeInfoId || selectedStore?.store_info_id || null,
    vendorId: selectedStore?.vendorId || selectedStore?.vendor_id || null,
    legacyStoreId: selectedStore?.legacyStoreId || selectedStore?.legacy_store_id || null,
    storeName: selectedStore?.name || selectedStore?.storeName || selectedStore?.storeCode || '',
  }), [selectedStore]);
  const selectedStoreLogId = selectedStoreContext.storeInfoId || selectedStoreContext.legacyStoreId || '';
  const hasStoreContext = Boolean(selectedStoreContext.storeInfoId && selectedStoreContext.vendorId);
  const canViewPage = hasFeatureAccess('goods_received.task');
  const canEditReceived = hasFeatureAccess('goods_received.task');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 800);
  const [fromDate, setFromDate] = useState(shiftDate(today(), -7));
  const [toDate, setToDate] = useState(today());
  const [orderedVsDeliveredFilter, setOrderedVsDeliveredFilter] = useState('all');
  const [deliveredVsReceivedFilter, setDeliveredVsReceivedFilter] = useState('all');
  const [orderedVsReceivedFilter, setOrderedVsReceivedFilter] = useState('all');
  const [orderNumberFilter, setOrderNumberFilter] = useState('all');
  const [, setApiMeta] = useState({ source: '', total: 0 });
  const autoSaveTimersRef = useRef({});
  const searchInputRef = useRef(null);
  const quantityInputRefs = useRef(new Map());
  const requestSequenceRef = useRef(0);

  const focusSearchInput = () => {
    window.requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });
  };

  useEffect(() => {
    if (!canViewPage) return;
    focusSearchInput();
  }, [canViewPage]);

  useEffect(() => {
    if (!canViewPage || !hasStoreContext) {
      setOrders([]);
      return;
    }

    const loadOrders = async () => {
      const requestId = requestSequenceRef.current + 1;
      requestSequenceRef.current = requestId;
      setLoading(true);
      try {
        const response = await logisticsOrdersAPI.getGoodsReceived({
          search: debouncedSearch,
          storeId: selectedStoreLogId,
          fromDate,
          toDate,
        });

        if (requestSequenceRef.current !== requestId) return;

        const nextOrders = (response.data?.data || []).map((order) => ({
          ...order,
          lineItems: (order.lineItems || []).map((item) => ({
            ...item,
            sku: item.sku || item.SKU || '',
            barcode: item.barcode || item.Barcode || '',
            product: item.product || item.productName || item.product_name || item.name || '',
            receivedQuantity: item.receivedQuantity ?? '',
          })),
        })).sort((a, b) => {
          const aTime = new Date(a.goodsReceivedUpdatedAt || a.updatedAt || a.createdAt || 0).getTime();
          const bTime = new Date(b.goodsReceivedUpdatedAt || b.updatedAt || b.createdAt || 0).getTime();
          return bTime - aTime;
        });

        setOrders(nextOrders);
        setApiMeta({
          source: response.data?.meta?.source || '',
          total: Number(response.data?.meta?.total || nextOrders.length || 0),
        });

        const searchQuery = normalizeSearchQuery(debouncedSearch);
        if (searchQuery && nextOrders.length === 0) {
          toast.error(`No product found for "${searchQuery}".`);
          focusSearchInput();
        }
      } catch (error) {
        if (requestSequenceRef.current !== requestId) return;
        toast.error(error?.response?.data?.message || 'Failed to load goods received orders');
        setApiMeta({ source: '', total: 0 });
      } finally {
        if (requestSequenceRef.current === requestId) {
          setLoading(false);
        }
      }
    };

    loadOrders();
  }, [canViewPage, debouncedSearch, fromDate, hasStoreContext, selectedStoreLogId, toDate]);

  useEffect(() => () => {
    Object.values(autoSaveTimersRef.current).forEach((timer) => clearTimeout(timer));
  }, []);

  const updateLineItem = (orderId, itemId, value) => {
    setOrders((current) => current.map((order) => {
      if (order.id !== orderId) return order;
      const nextLineItems = order.lineItems.map((item) => {
        if (item.id !== itemId) return item;
        const receivedQuantity = parseInputQuantity(value);
        const numericReceived = receivedQuantity === '' ? null : Number(receivedQuantity);
        const variance = computeDifference(numericReceived, item.orderQuantity);

        return {
          ...item,
          receivedQuantity,
          variance,
          receivedVsOrderedDifference: computeDifference(numericReceived, item.orderQuantity),
          receivedVsDeliveredDifference: computeDifference(numericReceived, item.deliveredQuantity),
          receiptStatus: computeReceiptStatus(item.orderQuantity, receivedQuantity),
        };
      });

      return {
        ...order,
        goodsReceivedStatus: computeOrderReceiptProgress(nextLineItems),
        lineItems: nextLineItems,
      };
    }));

    if (canEditReceived) {
      if (autoSaveTimersRef.current[orderId]) {
        clearTimeout(autoSaveTimersRef.current[orderId]);
      }

      autoSaveTimersRef.current[orderId] = setTimeout(() => {
        setOrders((latestOrders) => {
          const latestOrder = latestOrders.find((entry) => entry.id === orderId);
          if (latestOrder) {
            saveOrder(latestOrder, { silent: true });
          }
          return latestOrders;
        });
      }, 800);
    }
  };

  const saveOrder = async (order, options = {}) => {
    const { silent = false } = options;

    if (!canEditReceived) {
      toast.error('Only staff and supervisors can update received quantities.');
      return;
    }

    if (autoSaveTimersRef.current[order.id]) {
      clearTimeout(autoSaveTimersRef.current[order.id]);
      delete autoSaveTimersRef.current[order.id];
    }

    if (!order.lineItems?.length) {
      toast.error('No product rows were imported for this delivered order.');
      return;
    }

    try {
      const response = await logisticsOrdersAPI.updateGoodsReceived(order.id, {
        lineItems: toPayloadLineItems(order.lineItems),
      });
      const updatedOrder = response.data?.data;
      setOrders((current) => current.map((entry) => (
        entry.id === order.id
          ? {
              ...updatedOrder,
              lineItems: (updatedOrder.lineItems || []).map((item) => ({
                ...item,
                receivedQuantity: item.receivedQuantity ?? '',
              })),
            }
          : entry
      )));
      if (!silent) {
        toast.success('Goods received quantities saved');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save goods received quantities');
    }
  };

  const filteredOrders = useMemo(() => {
    const searchQuery = normalizeSearchComparable(debouncedSearch);

    return orders
      .map((order) => ({
        ...order,
        lineItems: (order.lineItems || []).filter((item) => {
          if (searchQuery && !matchesGoodsReceivedSearch(order, item, searchQuery)) return false;
          const orderedDeliveredStatus = getComparisonStatus(item.deliveredQuantity, item.orderQuantity);
          const deliveredReceivedStatus = getComparisonStatus(item.receivedQuantity === '' ? null : item.receivedQuantity, item.deliveredQuantity);
          const orderedReceivedStatus = getComparisonStatus(item.receivedQuantity === '' ? null : item.receivedQuantity, item.orderQuantity);

          if (orderedVsDeliveredFilter !== 'all' && orderedDeliveredStatus !== orderedVsDeliveredFilter) return false;
          if (deliveredVsReceivedFilter !== 'all' && deliveredReceivedStatus !== deliveredVsReceivedFilter) return false;
          if (orderedVsReceivedFilter !== 'all' && orderedReceivedStatus !== orderedVsReceivedFilter) return false;
          return true;
        }),
      }))
      .filter((order) => order.lineItems.length > 0)
  }, [debouncedSearch, deliveredVsReceivedFilter, orderedVsDeliveredFilter, orderedVsReceivedFilter, orders]);

  const orderNumberOptions = useMemo(() => ([
    { value: 'all', label: 'All Orders' },
    ...Array.from(new Set(filteredOrders.map((order) => String(order.orderNumber || '').trim()).filter(Boolean)))
      .sort((a, b) => a.localeCompare(b))
      .map((orderNumber) => ({ value: orderNumber, label: orderNumber })),
  ]), [filteredOrders]);

  const flatRows = useMemo(() => {
    const searchQuery = normalizeSearchComparable(search);
    return filteredOrders
      .filter((order) => orderNumberFilter === 'all' || String(order.orderNumber) === String(orderNumberFilter))
      .flatMap((order) => (
        (order.lineItems || []).map((item) => ({
          orderId: order.id,
          orderNumber: order.orderNumber,
          orderType: order.type,
          orderStatus: order.status,
          storeName: order.storeName,
          seller: order.seller,
          deliveredAt: order.deliveredAt,
          goodsReceivedStatus: order.goodsReceivedStatus,
          item,
        }))
      ))
      .filter((row) => {
        if (!searchQuery) return true;
        const rowText = normalizeSearchComparable([
          row.orderNumber,
          row.storeName,
          row.seller,
          row.item?.sku,
          row.item?.barcode,
          row.item?.product,
          row.item?.productName,
          row.item?.product_name,
          row.item?.name,
        ].join(' '));
        return rowText.includes(searchQuery);
      });
  }, [filteredOrders, orderNumberFilter, search]);

  const exportExcel = () => {
    const rows = filteredOrders.flatMap((order) => (
      (order.lineItems || []).map((item) => ({
        orderNumber: order.orderNumber,
        type: order.type,
        orderDate: order.orderedDate,
        storeName: order.storeName,
        seller: order.seller,
        deliveredOn: order.deliveredAt,
        sku: item.sku,
        product: item.product,
        orderedQty: item.orderQuantity,
        deliveredQty: item.deliveredQuantity,
        orderedVsDelivered: item.orderedVsDeliveredDifference,
        orderedVsDeliveredStatus: getComparisonStatus(item.deliveredQuantity, item.orderQuantity),
        receivedQty: item.receivedQuantity === '' ? null : item.receivedQuantity,
        receivedVsOrdered: item.receivedVsOrderedDifference,
        receivedVsOrderedStatus: getComparisonStatus(item.receivedQuantity === '' ? null : item.receivedQuantity, item.orderQuantity),
        receivedVsDelivered: item.receivedVsDeliveredDifference,
        receivedVsDeliveredStatus: getComparisonStatus(item.receivedQuantity === '' ? null : item.receivedQuantity, item.deliveredQuantity),
      }))
    ));

    downloadExcelWorkbook({
      fileName: `goods-received-${fromDate}-to-${toDate}.xlsx`,
      sheetName: 'Goods Received',
      columns: [
        { header: 'Order Number', key: 'orderNumber', width: 20 },
        { header: 'Type', key: 'type', width: 12, value: (row) => orderTypeLabel(row.type) },
        { header: 'Order Date', key: 'orderDate', width: 14 },
        { header: 'Store', key: 'storeName', width: 22 },
        { header: 'Seller', key: 'seller', width: 22 },
        { header: 'Delivered On', key: 'deliveredOn', width: 18 },
        { header: 'SKU', key: 'sku', width: 18 },
        { header: 'Product', key: 'product', width: 28 },
        { header: 'Ordered Qty', key: 'orderedQty', width: 14 },
        { header: 'Delivered Qty', key: 'deliveredQty', width: 14 },
        { header: 'Ordered vs Delivered', key: 'orderedVsDelivered', width: 18 },
        { header: 'Ordered vs Delivered Status', key: 'orderedVsDeliveredStatus', width: 22 },
        { header: 'Received Qty', key: 'receivedQty', width: 14 },
        { header: 'Received vs Ordered', key: 'receivedVsOrdered', width: 18 },
        { header: 'Received vs Ordered Status', key: 'receivedVsOrderedStatus', width: 22 },
        { header: 'Received vs Delivered', key: 'receivedVsDelivered', width: 18 },
        { header: 'Received vs Delivered Status', key: 'receivedVsDeliveredStatus', width: 22 },
      ],
      rows,
    });
  };

  if (!canViewPage) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Goods Received</h1>
        <div className="card">
          <p className="text-gray-500">Access denied.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Goods Received</h1>
          <p className="mt-1 text-sm text-gray-600">
            Logistics orders with uploaded product sheets appear here so store teams can verify items and confirm whether every product arrived in full.
          </p>
        </div>
        <ExportButton onClick={exportExcel} />
      </div>

      <div className="card">
        <div className="flex flex-wrap items-end gap-3">
          <label className="relative block min-w-[160px] flex-[0.75_1_180px]">
            <span className="label">Search</span>
            <Search className="pointer-events-none absolute left-3 top-[2.55rem] h-4 w-4 text-gray-400" />
            <input
              ref={searchInputRef}
              className="input pl-10"
              placeholder="Order, barcode, SKU, product..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
          <FilterSelect
            label="Ordered vs Delivered"
            value={orderedVsDeliveredFilter}
            options={COMPARISON_FILTER_OPTIONS}
            onChange={setOrderedVsDeliveredFilter}
            className="w-[142px]"
          />
          <FilterSelect
            label="Delivered vs Received"
            value={deliveredVsReceivedFilter}
            options={COMPARISON_FILTER_OPTIONS}
            onChange={setDeliveredVsReceivedFilter}
            className="w-[142px]"
          />
          <FilterSelect
            label="Ordered vs Received"
            value={orderedVsReceivedFilter}
            options={COMPARISON_FILTER_OPTIONS}
            onChange={setOrderedVsReceivedFilter}
            className="w-[142px]"
          />
          <FilterSelect
            label="Order Number"
            value={orderNumberFilter}
            options={orderNumberOptions}
            onChange={setOrderNumberFilter}
            className="w-[180px]"
          />

          <div className="w-[120px]">
            <label className="label">Date From</label>
            <input className="input text-sm" type="date" value={fromDate} max={toDate} onChange={(event) => setFromDate(event.target.value)} />
          </div>
          <div className="w-[120px]">
            <label className="label">Date To</label>
            <input className="input text-sm" type="date" value={toDate} min={fromDate} max={today()} onChange={(event) => setToDate(event.target.value)} />
          </div>
        </div>
        <p className="mt-3 text-xs text-gray-500">
          Showing store <span className="font-semibold text-gray-700">{selectedStoreContext.storeName || selectedStoreLogId || 'Select a store'}</span>
        </p>
      </div>

      <div className="card overflow-hidden p-0">
        <div className="table-scroll-shell">
          <table className="min-w-[1960px] divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="px-3 py-3 font-semibold">Order Number</th>
                <th className="px-3 py-3 font-semibold">Type</th>
                <th className="px-3 py-3 font-semibold">Order Status</th>
                <th className="px-3 py-3 font-semibold">Store</th>
                <th className="px-3 py-3 font-semibold">Seller</th>
                <th className="px-3 py-3 font-semibold">Delivered On</th>
                <th className="px-3 py-3 font-semibold">SKU</th>
                <th className="w-[20ch] min-w-[20ch] max-w-[20ch] px-3 py-3 font-semibold">Barcode</th>
                <th className="px-3 py-3 font-semibold">Product</th>
                <th className="px-3 py-3 font-semibold">Ordered Qty</th>
                <th className="px-3 py-3 font-semibold">Delivered Qty</th>
                <th className="px-3 py-3 font-semibold">Ordered vs Delivered</th>
                <th className="px-3 py-3 font-semibold">Ordered vs Delivered Status</th>
                <th className="px-3 py-3 font-semibold">Received Qty</th>
                <th className="px-3 py-3 font-semibold">Received vs Ordered</th>
                <th className="px-3 py-3 font-semibold">Ordered vs Received Status</th>
                <th className="px-3 py-3 font-semibold">Received vs Delivered</th>
                <th className="px-3 py-3 font-semibold">Delivered vs Received Status</th>
                <th className="px-3 py-3 font-semibold">Receipt Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {flatRows.map((row) => {
                const item = row.item;
                return (
                  <tr key={`${row.orderId}:${item.id}`}>
                    <td className="px-3 py-3 font-semibold text-gray-900">{row.orderNumber || '-'}</td>
                    <td className="px-3 py-3"><span className={`badge ${orderTypeBadgeClass(row.orderType)}`}>{orderTypeLabel(row.orderType)}</span></td>
                    <td className="px-3 py-3"><span className={`badge ${orderStatusBadgeClass(row.orderStatus)}`}>{orderStatusLabel(row.orderStatus)}</span></td>
                    <td className="px-3 py-3 text-gray-700">{row.storeName || '-'}</td>
                    <td className="px-3 py-3 text-gray-700">{row.seller || '-'}</td>
                    <td className="px-3 py-3 text-gray-700">
                      {row.deliveredAt ? formatDate(row.deliveredAt) : (
                        <span className="text-xs text-amber-600">Not delivered</span>
                      )}
                    </td>
                    <td className="px-3 py-3 font-medium text-gray-800">{item.sku || '-'}</td>
                    <td className="w-[20ch] min-w-[20ch] max-w-[20ch] px-3 py-3 text-gray-700">
                      <div className="barcode-scroll-cell" title={item.barcode || '-'}>{item.barcode || '-'}</div>
                    </td>
                    <td className="px-3 py-3 text-gray-700">{item.product || '-'}</td>
                    <td className="px-3 py-3 text-gray-700">{item.orderQuantity ?? '-'}</td>
                    <td className="px-3 py-3 text-gray-700">{item.deliveredQuantity ?? '-'}</td>
                    <td className={`px-3 py-3 ${item.orderedVsDeliveredDifference < 0 ? 'text-warning-700' : item.orderedVsDeliveredDifference > 0 ? 'text-danger-700' : 'text-gray-700'}`}>{item.orderedVsDeliveredDifference ?? '-'}</td>
                    <td className="px-3 py-3"><span className={`badge ${comparisonBadgeClass(getComparisonStatus(item.deliveredQuantity, item.orderQuantity))}`}>{getComparisonStatus(item.deliveredQuantity, item.orderQuantity)}</span></td>
                    <td className="px-3 py-3">
                      <input
                        ref={(node) => {
                          const key = `${row.orderId}:${item.id}`;
                          if (node) quantityInputRefs.current.set(key, node);
                          else quantityInputRefs.current.delete(key);
                        }}
                        className="input w-[90px] min-w-[90px] text-right font-mono tabular-nums"
                        type="number"
                        min="0"
                        value={item.receivedQuantity}
                        disabled={!canEditReceived || row.orderStatus === 'cancelled'}
                        title={row.orderStatus === 'cancelled' ? 'Cannot update cancelled orders' : ''}
                        onChange={(event) => updateLineItem(row.orderId, item.id, event.target.value)}
                      />
                    </td>
                    <td className={`px-3 py-3 ${item.receivedVsOrderedDifference < 0 ? 'text-warning-700' : item.receivedVsOrderedDifference > 0 ? 'text-danger-700' : 'text-gray-700'}`}>{item.receivedQuantity === '' || item.receivedVsOrderedDifference === null ? '-' : item.receivedVsOrderedDifference}</td>
                    <td className="px-3 py-3"><span className={`badge ${comparisonBadgeClass(getComparisonStatus(item.receivedQuantity === '' ? null : item.receivedQuantity, item.orderQuantity))}`}>{getComparisonStatus(item.receivedQuantity === '' ? null : item.receivedQuantity, item.orderQuantity)}</span></td>
                    <td className={`px-3 py-3 ${item.receivedVsDeliveredDifference < 0 ? 'text-warning-700' : item.receivedVsDeliveredDifference > 0 ? 'text-danger-700' : 'text-gray-700'}`}>{item.receivedQuantity === '' || item.receivedVsDeliveredDifference === null ? '-' : item.receivedVsDeliveredDifference}</td>
                    <td className="px-3 py-3"><span className={`badge ${comparisonBadgeClass(getComparisonStatus(item.receivedQuantity === '' ? null : item.receivedQuantity, item.deliveredQuantity))}`}>{getComparisonStatus(item.receivedQuantity === '' ? null : item.receivedQuantity, item.deliveredQuantity)}</span></td>
                    <td className="px-3 py-3"><span className={`badge ${receiptBadgeClass(row.goodsReceivedStatus)}`}>{row.goodsReceivedStatus}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {!loading && flatRows.length === 0 ? (
          <div className="card text-sm text-gray-500">
            No logistics orders with uploaded product sheets found for {selectedStoreContext.storeName || 'the selected store'} in the selected date range.
          </div>
      ) : null}
    </div>
  );
};

export default GoodsReceived;
